import { Cake, CheckCircle2, PartyPopper, Sparkles, X } from "lucide-react";

const IMAGE_BASE_URL = "https://uptask.visiontrek.io";

const getImageUrl = (path = "") => {
  if (!path) return `${IMAGE_BASE_URL}/default-avatar.png`;
  if (/^https?:\/\//i.test(path)) return path;
  return `${IMAGE_BASE_URL}${path.startsWith("/") ? path : `/${path}`}`;
};

export default function CelebrationLoginPopup({
  isOpen,
  events = [],
  sendingId,
  sentIds = new Set(),
  onClose,
  onSendCongratulations,
}) {
  if (!isOpen || events.length === 0) return null;

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-[#05010f]/78 px-4 backdrop-blur-xl">
      <div className="relative w-full max-w-lg overflow-hidden rounded-[32px] border border-white/14 bg-[#120a24] text-white shadow-[0_34px_120px_rgba(0,0,0,0.78),inset_0_1px_0_rgba(255,255,255,0.10)]">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_16%_0%,rgba(34,211,238,0.24),transparent_28%),radial-gradient(circle_at_84%_10%,rgba(244,114,182,0.24),transparent_30%),linear-gradient(135deg,rgba(255,255,255,0.10),transparent_38%,rgba(168,85,247,0.12))]" />
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          {Array.from({ length: 28 }).map((_, index) => (
            <span
              key={index}
              className="absolute h-2 w-1 rounded-full opacity-80 animate-[celebrationSprinkle_2.8s_linear_infinite]"
              style={{
                left: `${(index * 37) % 100}%`,
                top: `${-10 - ((index * 17) % 70)}px`,
                animationDelay: `${(index % 9) * 0.18}s`,
                backgroundColor: ["#22d3ee", "#a855f7", "#f472b6", "#facc15", "#34d399"][index % 5],
                transform: `rotate(${index * 19}deg)`,
              }}
            />
          ))}
        </div>

        <button
          type="button"
          onClick={onClose}
          className="absolute right-5 top-5 z-10 grid h-10 w-10 place-items-center rounded-2xl border border-white/12 bg-white/10 text-white/72 transition hover:bg-white/16 hover:text-white"
          aria-label="Close celebration popup"
        >
          <X size={18} />
        </button>

        <div className="relative px-6 pb-6 pt-7 sm:px-8 sm:pb-8">
          <div className="mx-auto grid h-16 w-16 place-items-center rounded-[24px] bg-gradient-to-br from-pink-400 via-violet-500 to-cyan-400 text-white shadow-[0_18px_48px_rgba(168,85,247,0.42)]">
            <PartyPopper size={30} />
          </div>

          <div className="mt-5 text-center">
            <p className="text-[11px] font-black uppercase tracking-[0.28em] text-cyan-100/68">
              Today&apos;s Celebration
            </p>
            <h2 className="mt-2 text-3xl font-black tracking-tight text-white">
              Make their day special
            </h2>
            <p className="mx-auto mt-2 max-w-sm text-sm font-medium leading-6 text-white/58">
              Send a quick congratulations and open their chat instantly.
            </p>
          </div>

          <div className="mt-6 space-y-3">
            {events.map((event) => {
              const isBirthday = event.eventType === "birthday" || event.type === "birthday";
              const personName = event.displayName || event.meta?.display_name || "Team member";
              const sent = sentIds.has(event.popupKey);
              const isSending = sendingId === event.popupKey;

              return (
                <div
                  key={event.popupKey}
                  className="relative overflow-hidden rounded-[24px] border border-white/12 bg-white/[0.075] p-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.08)]"
                >
                  <div className="pointer-events-none absolute inset-x-5 top-0 h-px bg-gradient-to-r from-transparent via-white/50 to-transparent" />
                  <div className="flex items-center gap-4">
                    <div className="relative shrink-0">
                      <img
                        src={getImageUrl(event.profilePic)}
                        alt={`${personName} profile`}
                        className="h-16 w-16 rounded-3xl object-cover ring-2 ring-white/18 shadow-[0_14px_32px_rgba(0,0,0,0.28)]"
                      />
                      <span className="absolute -bottom-1.5 -right-1.5 grid h-7 w-7 place-items-center rounded-2xl border border-white/18 bg-[#24163b] text-pink-100 shadow-lg">
                        {isBirthday ? <Cake size={15} /> : <Sparkles size={15} />}
                      </span>
                    </div>

                    <div className="min-w-0 flex-1">
                      <p className="text-[10px] font-black uppercase tracking-[0.18em] text-white/48">
                        {isBirthday ? "Birthday Today" : "Work Anniversary Today"}
                      </p>
                      <h3 className="mt-1 truncate text-xl font-black text-white">
                        {personName}
                      </h3>
                      <p className="mt-1 text-sm font-semibold text-white/58">
                        {event.detail}
                      </p>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => onSendCongratulations(event)}
                    disabled={sent || isSending}
                    className={`mt-4 inline-flex w-full items-center justify-center gap-2 rounded-2xl px-4 py-3 text-sm font-black transition ${
                      sent
                        ? "bg-emerald-400/15 text-emerald-100"
                        : "bg-gradient-to-r from-fuchsia-500 via-violet-500 to-cyan-400 text-white shadow-[0_18px_42px_rgba(168,85,247,0.34)] hover:-translate-y-0.5"
                    } disabled:cursor-not-allowed disabled:opacity-75`}
                  >
                    {sent ? <CheckCircle2 size={17} /> : <Sparkles size={17} />}
                    {sent ? "Congratulations sent" : isSending ? "Sending..." : "Send congratulations"}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
